import { IShardRepository, IUpdateShardRepositoryRequest, StorageType } from "api/entities/shard-repository";
import { getRetentionSettings, IRetentionSettingsV2, retentionPolicyToTextMap } from "components/buckets/sections/retention-section/retention-section-v2";
import React, { ReactNode } from "react";
import { storageUtils } from "utils/storage-utils";
import { IAccessCredentialListItem } from "api/entities/access-credential";
import { IBucketDetailsItem, IBucketUpdateRequest } from "api/entities/bucket";
import { IImportValidationStatus, IImportValidationSuccessStatus, ImportEntityType, importEntityTypeDict } from "api/entities/import-bzs";
import { IShardClusterDetailsItem } from "api/entities/shard-cluster";
import { InfoDialog } from "components/shared-ui/dialogs/common/info-dialog/info-dialog";
import { FormFieldLabel } from "components/shared-ui/form/form-field-label/form-field-label";
import { FormFieldValue } from "components/shared-ui/form/form-field-value/form-field-value";
import { PoolStorageNodesInfoSection } from "../../../buckets/sections/pool-storage-nodes-info-section/pool-storage-nodes-info-section";
import styles from "./import-bzs-entity-comparison-dialog.module.scss";
import SidePanelSection from "components/shared-ui/side-panel-section/side-panel-section";

export interface IImportBzsEntityComparisonResultDialogProps {
    isOpen: boolean;
    importInfo: IImportValidationStatus | IImportValidationSuccessStatus;
    onClose: () => void;
}

export function ImportBzsEntityComparisonDialog(props: IImportBzsEntityComparisonResultDialogProps) {
    const entityName = !!props.importInfo && "entityType" in props.importInfo
        ? importEntityTypeDict[props.importInfo.entityType]
        : "";

    function getStorageNodeEntity(storageNode: IShardRepository | IUpdateShardRepositoryRequest, showIdColumn: boolean = true) {
        let storageType: StorageType = storageNode.storageType;

        return <>
            {showIdColumn &&
                <div>
                    <FormFieldLabel noLeftMargin={true}>
                        Id
                    </FormFieldLabel>
                    <FormFieldValue>
                        <label>
                            {storageNode.id}
                        </label>
                    </FormFieldValue>
                </div>
            }

            <div>
                <FormFieldLabel noLeftMargin={true}>
                    Storage provider
                </FormFieldLabel>
                <FormFieldValue>
                    <label>
                        {storageUtils.getStorageProviderTypeGeneralizedType(storageType)}
                    </label>
                </FormFieldValue>
            </div>

            <div>
                <FormFieldLabel noLeftMargin={true}>
                    Bucket
                </FormFieldLabel>
                <FormFieldValue>
                    <label>
                        {storageNode.bucket}
                    </label>
                </FormFieldValue>
            </div>

            {!!storageNode.region &&
                <div>
                    <FormFieldLabel noLeftMargin={true}>
                        Region
                    </FormFieldLabel>
                    <FormFieldValue>
                        <label>
                            {storageNode.region}
                        </label>
                    </FormFieldValue>
                </div>
            }
            {!!storageNode.endpointURL &&
                <div>
                    <FormFieldLabel noLeftMargin={true}>
                        Endpoint URL
                    </FormFieldLabel>
                    <FormFieldValue>
                        <label>
                            { storageNode.endpointURL }
                        </label>
                    </FormFieldValue>
                </div>
            }

        </>;
    }

    function getStoragePoolEntity(storagePool: IShardClusterDetailsItem) {

        return <>
            <div>
                <FormFieldLabel noLeftMargin={true}>
                    Id
                </FormFieldLabel>
                <FormFieldValue>
                    <label>
                        {storagePool.id}
                    </label>
                </FormFieldValue>
            </div>

            <div>
                <FormFieldLabel noLeftMargin={true}>
                    Name
                </FormFieldLabel>
                <FormFieldValue>
                    <label>
                        {storagePool.name}
                    </label>
                </FormFieldValue>
            </div>

            <PoolStorageNodesInfoSection storagePool={storagePool}/>
        </>;
    }

    function getBucketEntity(bucket: IBucketDetailsItem | IBucketUpdateRequest) {

        function getBucketRetentionPolicyDescription(retentionSettings: IRetentionSettingsV2): string {
            return retentionSettings.deleteFiles
                ? `Delete files after ${retentionSettings.retentionDays} days`
                : "Keep all versions of files";
        }

        let retentionSettings: IRetentionSettingsV2;

        if ("bucketConfig" in bucket) {
            retentionSettings = getRetentionSettings(bucket.bucketConfig);
        }

        const lifecyclePolicy = getBucketRetentionPolicyDescription(retentionSettings);

        return <>
            <div>
                <FormFieldLabel noLeftMargin={true}>
                    Id
                </FormFieldLabel>
                <FormFieldValue>
                    <label>
                        {bucket.id}
                    </label>
                </FormFieldValue>
            </div>

            <div>
                <FormFieldLabel noLeftMargin={true}>
                    Name
                </FormFieldLabel>
                <FormFieldValue>
                    <label>
                        {bucket.name}
                    </label>
                </FormFieldValue>
            </div>

            <div>
                <FormFieldLabel noLeftMargin={true}>
                    Object Lock
                </FormFieldLabel>
                <FormFieldValue>
                    <label>
                        {bucket.objectLock ? "Yes" : "No"}
                    </label>
                </FormFieldValue>
            </div>

            <div>
                <FormFieldLabel noLeftMargin={true}>
                    Object Versioning
                </FormFieldLabel>
                <FormFieldValue>
                    <label>
                        {bucket.objectVersioning ? "Yes" : "No"}
                    </label>
                </FormFieldValue>
            </div>

            <div>
                <FormFieldLabel noLeftMargin={true}>
                    Lifecycle Policy
                </FormFieldLabel>
                <FormFieldValue>
                    <label>
                        {lifecyclePolicy}
                    </label>
                </FormFieldValue>
            </div>
        </>;
    }

    function getAccessCredentialEntity(accessCredential: IAccessCredentialListItem) {

        return <>
            <div>
                <FormFieldLabel noLeftMargin={true}>
                    Id
                </FormFieldLabel>
                <FormFieldValue>
                    <label>
                        {accessCredential.id}
                    </label>
                </FormFieldValue>
            </div>

            <div>
                <FormFieldLabel noLeftMargin={true}>
                    Name
                </FormFieldLabel>
                <FormFieldValue>
                    <label>
                        {accessCredential.name}
                    </label>
                </FormFieldValue>
            </div>

            <div>
                <FormFieldLabel noLeftMargin={true}>
                    Scope
                </FormFieldLabel>
                <FormFieldValue>
                    <label className={styles.capitalize}>
                        {accessCredential.scope}
                    </label>
                </FormFieldValue>
            </div>
            <div>
                <FormFieldLabel noLeftMargin={true}>
                    Bucket Id
                </FormFieldLabel>
                <FormFieldValue>
                    <label>
                        {accessCredential.bucketId}
                    </label>
                </FormFieldValue>
            </div>

        </>;
    }

    function getTwoColumnLayout(leftColumn: ReactNode, rightColumn: ReactNode): ReactNode {
        return <section className={styles.row}>
            <section className={styles.left}>
                {leftColumn}
            </section>
            <section className={styles.right}>
                {rightColumn}
            </section>
        </section>;
    }

    function getConflictingEntitiesLayout(x: any, index: number): ReactNode {
        if (!("entityType" in props.importInfo
            && "conflictingEntities" in props.importInfo)) {
            return <></>;
        }

        let conflictingEntities: any[] = props.importInfo.conflictingEntities;
        const conflictingEntity = index < conflictingEntities.length ? conflictingEntities[index] : null;

        let layout: ReactNode = <></>;
        if (!conflictingEntity) {
            return layout;
        }

        switch (props.importInfo.entityType){
            case ImportEntityType.StorageNode:
                layout = getTwoColumnLayout(
                    getStorageNodeEntity(x as IUpdateShardRepositoryRequest),
                    getStorageNodeEntity(conflictingEntity as IShardRepository)
                );
                break;

            case ImportEntityType.Bucket:
                layout = getTwoColumnLayout(
                    getBucketEntity(x as IBucketUpdateRequest),
                    getBucketEntity(conflictingEntity as IBucketDetailsItem)
                );

                break;

            case ImportEntityType.StoragePool:
                layout = getTwoColumnLayout(
                    getStoragePoolEntity(x as IShardClusterDetailsItem),
                    getStoragePoolEntity(conflictingEntity as IShardClusterDetailsItem)
                );

                break;
        }

        return layout;
    }

    function getImportedEntitiesLayout(importInfo: IImportValidationSuccessStatus): ReactNode {
        return <>
            <section>
                <SidePanelSection
                    testId="storage-node-section"
                    title="Storage Nodes"
                    className={styles["shard-repositories-block"]}
                >
                    {importInfo.importedEntities.storageNodes.map((x, i) =>
                        <section className={styles["row-block"]}>
                            <section className={styles.row}>
                                <div>{i + 1}</div>
                                <div className={styles["id-cell"]}>Id: {x.id}</div>
                                <div className={styles.spacer} />
                            </section>

                            <section className={styles.row}>
                                {getStorageNodeEntity(x, false)}
                            </section>
                        </section>
                    )}
                </SidePanelSection>
            </section>

            <section>
                <SidePanelSection
                    testId="storage-node-section"
                    title="Storage Pool"
                >
                    {getStoragePoolEntity(importInfo.importedEntities.storagePool)}
                </SidePanelSection>
            </section>

            <section>
                <SidePanelSection
                    testId="storage-node-section"
                    title="Bucket"
                >
                    {getBucketEntity(importInfo.importedEntities.bucket)}
                </SidePanelSection>
            </section>

            <section>
                <SidePanelSection
                    testId="storage-node-section"
                    title="Access Credential"
                >
                    {getAccessCredentialEntity(importInfo.importedEntities.accessCredential)}
                </SidePanelSection>
            </section>
        </>;

    }

    return (
        <InfoDialog
            testId='import-results-dialog'
            title={!!entityName
                ? `${entityName} comparison`
                : `Imported Entities`
            }
            showOkButton={true}
            open={props.isOpen}
            onClose={() => {
                props.onClose();
            }}
        >
            <div className={styles.content}>
                {
                    !!props.importInfo
                        ? "importEntities" in props.importInfo
                            ? props.importInfo.importEntities.map((x, i) => getConflictingEntitiesLayout(x, i))
                            : getImportedEntitiesLayout(props.importInfo as IImportValidationSuccessStatus)
                        : ''
                }
            </div>
        </InfoDialog>
    );
}
